/**
 * useNavigation Hook
 *
 * Provides dynamic navigation items built from the route registry.
 * Automatically updates when plugins register/unregister routes.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRouteRegistry } from '@lognai/frontend-core';
import {
  buildNavigationFromRoutes,
  getActiveRouteId,
  NavItemConfig,
  PluginNavMetadata,
} from '@lognai/frontend-core';
import { useAuth } from './useAuth';
import { useIsExternalPartner } from './useIsExternalPartner';

/**
 * Plugin metadata configuration for navigation grouping.
 * Defines display order and names for plugin groups in the sidebar.
 */
const PLUGIN_NAV_METADATA: Record<string, PluginNavMetadata> = {
  // Collaboration leads the sidebar — it is the cross-cutting surface, not one
  // domain among many.
  tasks: { displayName: 'Collaboration', order: 0, icon: 'CheckSquare' },
  products: { displayName: 'Products', order: 1, icon: 'Box' },
  procurement: { displayName: 'Procurement', order: 2, icon: 'ClipboardList' },
  orders: { displayName: 'SAP SOD', order: 3, icon: 'ShoppingCart' },
  warehouse: { displayName: 'Warehouse', order: 4, icon: 'Warehouse' },
  inventory: { displayName: 'VMI Inventory', order: 5, icon: 'Package' },
  rfq: { displayName: 'RFQ', order: 6, icon: 'FileText' },
  shipments: { displayName: 'Shipments', order: 7, icon: 'Truck' },
  tracking: { displayName: 'Track and Trace', order: 7.5, icon: 'Radar' },
  'landed-cost': { displayName: 'VLC Calculator', order: 8, icon: 'Calculator' },
  finance: { displayName: 'Finance', order: 9, icon: 'DollarSign' },
  'vendor-onboarding': { displayName: 'Vendor Onboarding', order: 10, icon: 'UserPlus' },
  analytics: { displayName: 'Analytics', order: 11, icon: 'BarChart3' },
  admin: { displayName: 'Admin', order: 100, icon: 'Settings' },
};

interface UseNavigationOptions {
  /** Whether to group nav items by plugin with separators (default: true) */
  groupByPlugin?: boolean;
}

interface UseNavigationResult {
  /** Main navigation items */
  navItems: NavItemConfig[];

  /** Footer navigation items */
  footerItems: NavItemConfig[];

  /** ID of the currently active nav item */
  activeItemId?: string;

  /** Whether navigation is loading */
  isLoading: boolean;
}

/**
 * Hook to get dynamic navigation items from route registry
 *
 * @param options - Navigation options
 */
export function useNavigation(options: UseNavigationOptions = {}): UseNavigationResult {
  const { groupByPlugin = true } = options;

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isExternalPartner = useIsExternalPartner();
  const [navItems, setNavItems] = useState<NavItemConfig[]>([]);
  const [footerItems, setFooterItems] = useState<NavItemConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Memoize plugin metadata to avoid unnecessary re-renders
  const pluginMetadata = useMemo(() => PLUGIN_NAV_METADATA, []);

  // Navigation handler
  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  // Build navigation from routes
  const buildNavigation = useCallback(() => {
    try {
      // Get user permissions
      const userPermissions = user?.permissions || [];

      // Build main navigation items from routes
      const items = buildNavigationFromRoutes({
        userPermissions,
        userAudience: isExternalPartner ? 'partner' : 'internal',
        currentPath: location.pathname,
        onNavigate: handleNavigate,
        groupByPlugin,
        pluginMetadata,
      });

      setNavItems(items);

      // TODO: Build footer items from routes
      // For now, keep footer items empty (will be set by MainLayout)
      setFooterItems([]);

      setIsLoading(false);
    } catch (error) {
      console.error('[useNavigation] Error building navigation:', error);
      setNavItems([]);
      setFooterItems([]);
      setIsLoading(false);
    }
  }, [location.pathname, user?.permissions, isExternalPartner, handleNavigate, groupByPlugin, pluginMetadata]);

  // Subscribe to route registry changes
  useEffect(() => {
    const registry = getRouteRegistry();

    // Build initial navigation
    buildNavigation();

    // Subscribe to route changes
    const unsubscribe = registry.subscribe(() => {
      console.log('[useNavigation] Routes changed, rebuilding navigation');
      buildNavigation();
    });

    return unsubscribe;
  }, [buildNavigation]);

  // Determine active item from current path
  const activeItemId = getActiveRouteId(location.pathname);

  return {
    navItems,
    footerItems,
    activeItemId,
    isLoading,
  };
}
